import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  TouchableOpacity,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
  StyleSheet,
  Image,
  Alert,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { palette } from '../../utils/palette';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import Text from '../../components/common/Text';
import { RootStackParamList } from '../../types/navigation';
import { useAuth } from '../../hooks/useAuth';
import TextField from '../../components/common/TextField';
import Button from '../../components/common/Button';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import ResponsiveContainer from '../../components/shared/ResponsiveContainer';
import EyeIcon from '../../components/common/EyeIcon';
import { useTheme } from '../../hooks/useTheme';
import { API_URL } from '../../constants/config';

// Form validation schema
const adminLoginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

type AdminLoginFormData = z.infer<typeof adminLoginSchema>;

const AdminLoginScreen = () => {
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const route = useRoute<RouteProp<RootStackParamList, 'AdminLogin'>>();
  const routeHash = route.params?.hash;
  const { signIn, isLoading, user } = useAuth();
  useSafeAreaInsets();
  const { isMobile, screenWidth } = useResponsiveLayout();
  const { colors } = useTheme();
  const [loginError, setLoginError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const emailInputRef = useRef<{ focus: () => void } | null>(null);

  // Responsive values
  const isTablet = screenWidth >= 768 && screenWidth < 1024;
  const logoSize = isMobile ? 40 : isTablet ? 50 : 60;
  const formPadding = isMobile ? 24 : isTablet ? 40 : 50;

  // Check if hash is provided
  useEffect(() => {
    if (!routeHash) {
      Alert.alert(
        'Access Denied',
        'Admin route hash is required. Please use the correct admin portal URL.',
        [
          {
            text: 'OK',
            onPress: () => {
              if (Platform.OS === 'web') {
                window.location.href = '/';
              } else {
                navigation.replace('Landing');
              }
            },
          },
        ]
      );
    }
  }, [routeHash, navigation]);

  // Redirect to admin dashboard if user is already logged in as admin
  useEffect(() => {
    if (user?.is_admin && routeHash) {
      navigation.replace('Admin', { hash: routeHash });
    }
  }, [user, navigation, routeHash]);

  // Focus email input when component mounts
  useEffect(() => {
    const focusEmail = () => {
      if (emailInputRef.current) {
        emailInputRef.current.focus();
      } else {
        setTimeout(focusEmail, 50);
      }
    };
    const timer = setTimeout(focusEmail, 200);
    return () => clearTimeout(timer);
  }, []);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<AdminLoginFormData>({
    resolver: zodResolver(adminLoginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (data: AdminLoginFormData) => {
    try {
      setLoginError(null);

      // First, sign in
      await signIn(data.email, data.password);

      // Wait for user state to update, then check admin status
      setTimeout(async () => {
        try {
          // Use cookie-aware fetch (works with both Bearer token and httpOnly cookies)
          const { apiFetchWithTokenRefresh } =
            await import('../../utils/tokenInterceptor');
          const response = await apiFetchWithTokenRefresh(
            `${API_URL}/auth/me`,
            {
              headers: { 'Content-Type': 'application/json' },
            }
          );

          if (response.ok) {
            const userData = await response.json();

            if (userData.is_admin) {
              navigation.replace('Admin', { hash: routeHash });
            } else {
              // User doesn't have admin permission - keep them on the page without message
              setLoginError(null);
            }
          } else {
            await response.json().catch(() => ({ detail: 'Unknown error' }));
            setLoginError(`Invalid email or password.`);
          }
        } catch {
          setLoginError('Invalid email or password.');
        }
      }, 500);
    } catch {
      setLoginError('Invalid email or password.');
    }
  };

  return (
    <ResponsiveContainer
      style={
        [styles.container, { backgroundColor: colors.authBackground }] as [
          object,
          object,
        ]
      }
    >
      <KeyboardAvoidingView
        style={[styles.keyboardAvoidingView, { zIndex: 10 }]}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContentContainer}
          showsVerticalScrollIndicator={false}
          showsHorizontalScrollIndicator={false}
          bounces={false}
        >
          <View
            style={[
              styles.contentContainer,
              !isMobile && styles.webContentContainer,
            ]}
          >
            {/* Professional Form Card */}
            <View
              style={[
                styles.formContainer,
                {
                  backgroundColor: colors.card,
                  shadowColor: colors.border,
                  borderColor: colors.borderLight,
                  paddingHorizontal: formPadding,
                },
              ]}
            >
              {/* Form Header */}
              <View style={styles.formHeader}>
                <TouchableOpacity
                  onPress={() => navigation.navigate('Landing')}
                  style={styles.logoContainer}
                >
                  <Image
                    source={require('../../../assets/logo-square.png')}
                    style={{ width: logoSize, height: logoSize }}
                    resizeMode="contain"
                  />
                </TouchableOpacity>
                <Text
                  size="md"
                  weight="bold"
                  style={[styles.formTitle, { color: colors.primary }]}
                >
                  Admin Login
                </Text>
                <Text
                  size="sm"
                  style={[styles.formSubtitle, { color: colors.secondary }]}
                >
                  Sign in to access the admin dashboard
                </Text>
              </View>

              <View style={styles.form}>
                <Controller
                  control={control}
                  name="email"
                  render={({ field: { onChange, onBlur, value } }) => (
                    <TextField
                      ref={emailInputRef}
                      label="Email Address"
                      placeholder="Enter your admin email"
                      keyboardType="email-address"
                      autoCapitalize="none"
                      value={value}
                      onChangeText={onChange}
                      onBlur={onBlur}
                      error={errors.email?.message}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name="password"
                  render={({ field: { onChange, onBlur, value } }) => (
                    <TextField
                      label="Password"
                      placeholder="Enter your password"
                      secureTextEntry={!showPassword}
                      value={value}
                      onChangeText={onChange}
                      onBlur={onBlur}
                      error={errors.password?.message}
                      rightIcon={<EyeIcon isVisible={showPassword} />}
                      onRightIconPress={() => setShowPassword(!showPassword)}
                      autoCapitalize="none"
                    />
                  )}
                />
              </View>

              {/* Error Display */}
              {loginError && (
                <View
                  style={[
                    styles.errorContainer,
                    { backgroundColor: colors.errorBackground },
                  ]}
                >
                  <Text
                    size="sm"
                    weight="medium"
                    style={[styles.errorText, { color: colors.errorText }]}
                  >
                    {loginError}
                  </Text>
                </View>
              )}

              {/* Sign In Button */}
              <Button
                title="Sign In"
                onPress={handleSubmit(onSubmit)}
                loading={isLoading}
                style={styles.signInButton}
              />

              {/* Footer */}
              <View style={styles.footer}>
                <Text
                  size="md"
                  style={[styles.footerText, { color: colors.secondary }]}
                >
                  Not an admin?{' '}
                </Text>
                <TouchableOpacity onPress={() => navigation.navigate('Login')}>
                  <Text
                    size="md"
                    weight="semibold"
                    style={[styles.footerLink, { color: colors.primary }]}
                  >
                    Regular Login
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </ResponsiveContainer>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    ...(Platform.OS === 'web'
      ? { backgroundImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }
      : {}),
  },
  scrollView: {
    flex: 1,
  },
  scrollContentContainer: {
    flexGrow: 1,
    justifyContent: 'center',
    minHeight: '100%',
    maxHeight: '100%',
  },
  contentContainer: {
    flex: 1,
    padding: 20,
    justifyContent: 'center',
  },
  formContainer: {
    borderRadius: 20,
    paddingHorizontal: 50,
    paddingVertical: 35,
    minHeight: 320,
    shadowOffset: {
      width: 0,
      height: 12,
    },
    shadowOpacity: 0.3,
    shadowRadius: 25,
    elevation: 12,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.2)',
  },
  formHeader: {
    marginBottom: 12,
    alignItems: 'center',
  },
  logoContainer: {
    marginBottom: 8,
    padding: 8,
    borderRadius: 12,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
  formTitle: {
    fontFamily:
      Platform.OS === 'web'
        ? 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        : 'System',
    fontSize: 16,
    fontWeight: '800',
    marginBottom: 4,
    letterSpacing: -0.8,
    textShadowColor: 'rgba(0, 0, 0, 0.1)',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 4,
  },
  formSubtitle: {
    fontFamily:
      Platform.OS === 'web'
        ? 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        : 'System',
    fontSize: 17,
    opacity: 0.8,
    fontWeight: '500',
    letterSpacing: 0.2,
    marginTop: 4,
  },
  form: {
    marginBottom: 12,
    gap: 2,
  },
  errorContainer: {
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
    borderLeftWidth: 4,
    borderLeftColor: palette.brightMatteRed,
  },
  errorText: {
    fontFamily:
      Platform.OS === 'web'
        ? 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        : 'System',
    textAlign: 'center',
  },
  signInButton: {
    marginBottom: 24,
    height: 52,
    borderRadius: 16,
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.2,
    shadowRadius: 8,
    elevation: 6,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 16,
  },
  footerText: {
    fontFamily:
      Platform.OS === 'web'
        ? 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        : 'System',
  },
  footerLink: {
    fontFamily:
      Platform.OS === 'web'
        ? 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        : 'System',
  },
  keyboardAvoidingView: {
    flex: 1,
  },
  webContentContainer: {
    width: '55%',
    alignSelf: 'center',
    maxWidth: 520,
    minWidth: 400,
  },
});

export default AdminLoginScreen;
